import { useState, type FormEvent, type ReactNode } from "react";
import { apiFetch } from "../api/client";
import {
  emailMessage,
  formatPhoneInput,
  isValidEmail,
  isValidPhone,
  phoneMessage,
  requiredMessage,
} from "../utils/formValidation";
import { redirectToPayPalApprove } from "../vendors/payment/paypalCheckout";

type TrialResult = {
  id: string;
  message?: string;
  company_name?: string;
  email?: string;
  product_label?: string;
};

type ServerOrderStart = {
  paypal?: { approve_url?: string };
  success?: boolean;
  error?: string;
  detail?: string;
  field_errors?: Record<string, string>;
};

const PRODUCT_OPTIONS = [
  {
    id: "website_server",
    label: "Website Server",
    note: "Dedicated AWS small to host your website.",
  },
  {
    id: "point_of_sale",
    label: "Point of Sale (POS)",
    note: "In-store POS — 15 day refundable free trial.",
  },
  {
    id: "ecommerce_cart",
    label: "Ecommerce cart",
    note: "Online cart — 15 day refundable free trial.",
  },
] as const;

export type PosProductId = (typeof PRODUCT_OPTIONS)[number]["id"];

const SERVER_REFUND_DISCLAIMER =
  "$20 refundable within 5 days minus $5 processing fees. Cancel before provisioning completes to request a refund.";

const TRIAL_DISCLAIMER =
  "15 day refundable free trial. If you cancel within the trial window, unused fees are refundable per our terms. You will be alerted when setup is complete.";

function isWebsiteServer(id: string) {
  return id === "website_server";
}

function ReqMark() {
  return (
    <span className="req-star" aria-hidden="true">
      *
    </span>
  );
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="field-error" role="alert">
      {message}
    </p>
  );
}

type Props = {
  /** When set (e.g. on /pos), hide the product dropdown and lock to that product. */
  lockedProduct?: PosProductId;
};

export function BuyPosForm({ lockedProduct }: Props) {
  const [product, setProduct] = useState<string>(lockedProduct ?? "point_of_sale");
  const [companyName, setCompanyName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [telephone, setTelephone] = useState("");
  const [username, setUsername] = useState("");
  const [companyCode, setCompanyCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [result, setResult] = useState<TrialResult | null>(null);

  const productId = lockedProduct ?? product;
  const isServer = isWebsiteServer(productId);
  const selected = PRODUCT_OPTIONS.find((p) => p.id === productId);
  const showProductSelect = !lockedProduct;

  function clearField(key: string) {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function labeled(text: string, required: boolean): ReactNode {
    return (
      <>
        {text}
        {required ? <ReqMark /> : null}
      </>
    );
  }

  function validate(): Record<string, string> {
    const errs: Record<string, string> = {};
    if (showProductSelect && !productId) errs.product = requiredMessage("Service");

    if (isServer) {
      if (!firstName.trim()) errs.first_name = requiredMessage("First name");
      if (!lastName.trim()) errs.last_name = requiredMessage("Last name");
      if (!email.trim()) errs.email = requiredMessage("Email address");
      else if (!isValidEmail(email)) errs.email = emailMessage("Email address");
      if (!username.trim()) errs.username = requiredMessage("Username");
      else if (!isValidEmail(username)) errs.username = emailMessage("Username");
      if (telephone.trim() && !isValidPhone(telephone)) errs.telephone = phoneMessage("Cell phone");
      return errs;
    }

    if (!companyName.trim()) errs.company_name = requiredMessage("Company");
    if (!firstName.trim()) errs.first_name = requiredMessage("First name");
    if (!lastName.trim()) errs.last_name = requiredMessage("Last name");
    if (!email.trim()) errs.email = requiredMessage("Email");
    else if (!isValidEmail(email)) errs.email = emailMessage();
    if (telephone.trim() && !isValidPhone(telephone)) errs.telephone = phoneMessage("Telephone");
    if (!companyCode.trim()) errs.company_code = requiredMessage("Company code");
    else if (!/^[a-z0-9]{4,20}$/.test(companyCode.trim())) {
      errs.company_code = "Company code must be 4–20 lowercase letters and numbers only.";
    }
    return errs;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      const first = Object.values(errs)[0];
      setError(
        `Please fix ${Object.keys(errs).length} field${Object.keys(errs).length === 1 ? "" : "s"}: ${first}`,
      );
      return;
    }

    setLoading(true);
    try {
      if (isServer) {
        const res = await apiFetch("/api/servers/orders", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            server_type: "website_server",
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            email: email.trim(),
            username: username.trim() || email.trim(),
            cell_phone: telephone.trim() || undefined,
          }),
        });
        const data = (await res.json().catch(() => ({}))) as ServerOrderStart;
        if (!res.ok || data.success === false) {
          if (data.field_errors) setFieldErrors(data.field_errors);
          throw new Error(
            data.detail || data.error || "We could not start PayPal checkout right now.",
          );
        }
        redirectToPayPalApprove(data.paypal?.approve_url);
        return;
      }

      const res = await apiFetch("/api/pos/trials", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          product: productId,
          company_name: companyName.trim(),
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim(),
          telephone: telephone.trim() || undefined,
          company_code: companyCode.trim().toLowerCase(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        if (data.field_errors && typeof data.field_errors === "object") {
          setFieldErrors(data.field_errors as Record<string, string>);
        }
        throw new Error(data.error || "We could not submit your trial right now.");
      }
      setResult(data.trial as TrialResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  if (result) {
    return (
      <div className="deposit-success">
        <h3>You are on the list</h3>
        <p>
          Thanks
          {result.company_name ? (
            <>
              , <strong>{result.company_name}</strong>
            </>
          ) : null}
          . We will email <strong>{result.email}</strong>
          {result.product_label ? (
            <>
              {" "}
              about your <strong>{result.product_label}</strong> trial
            </>
          ) : (
            <> when your trial</>
          )}{" "}
          is ready.
        </p>
        {result.message && <p>{result.message}</p>}
      </div>
    );
  }

  return (
    <form className="deposit-form buy-server-form buy-pos-form" onSubmit={onSubmit} noValidate>
      {showProductSelect ? (
        <div className="buy-server-row">
          <label htmlFor="pos-product">{labeled("Service", true)}</label>
          <div className="buy-server-control">
            <select
              id="pos-product"
              required
              aria-required="true"
              aria-invalid={Boolean(fieldErrors.product)}
              value={product}
              onChange={(e) => {
                setProduct(e.target.value);
                clearField("product");
                setError(null);
              }}
              disabled={loading}
            >
              {PRODUCT_OPTIONS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
            <FieldError message={fieldErrors.product} />
            {selected && (
              <p className="buy-server-product-note form-disclaimer form-disclaimer--under-select">
                {isServer ? SERVER_REFUND_DISCLAIMER : TRIAL_DISCLAIMER}
              </p>
            )}
          </div>
        </div>
      ) : (
        <p className="form-disclaimer form-disclaimer--under-select">{TRIAL_DISCLAIMER}</p>
      )}

      {isServer ? (
        <>
          <div className="buy-server-row">
            <label htmlFor="pos-server-first">{labeled("First name", true)}</label>
            <div className="buy-server-control">
              <input
                id="pos-server-first"
                type="text"
                required
                aria-required="true"
                aria-invalid={Boolean(fieldErrors.first_name)}
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  clearField("first_name");
                }}
                placeholder="Jane"
                autoComplete="given-name"
              />
              <FieldError message={fieldErrors.first_name} />
            </div>
          </div>
          <div className="buy-server-row">
            <label htmlFor="pos-server-last">{labeled("Last name", true)}</label>
            <div className="buy-server-control">
              <input
                id="pos-server-last"
                type="text"
                required
                aria-required="true"
                aria-invalid={Boolean(fieldErrors.last_name)}
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  clearField("last_name");
                }}
                placeholder="Smith"
                autoComplete="family-name"
              />
              <FieldError message={fieldErrors.last_name} />
            </div>
          </div>
          <div className="buy-server-row">
            <label htmlFor="pos-server-email">{labeled("Email address", true)}</label>
            <div className="buy-server-control">
              <input
                id="pos-server-email"
                type="email"
                required
                aria-required="true"
                aria-invalid={Boolean(fieldErrors.email)}
                value={email}
                onChange={(e) => {
                  const v = e.target.value;
                  setEmail(v);
                  setUsername((prev) => (prev ? prev : v));
                  clearField("email");
                }}
                placeholder="you@company.com"
                autoComplete="email"
              />
              <FieldError message={fieldErrors.email} />
            </div>
          </div>
          <div className="buy-server-row">
            <label htmlFor="pos-server-username">{labeled("Username", true)}</label>
            <div className="buy-server-control">
              <input
                id="pos-server-username"
                type="email"
                required
                aria-required="true"
                aria-invalid={Boolean(fieldErrors.username)}
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  clearField("username");
                }}
                placeholder="user@example.com"
              />
              <FieldError message={fieldErrors.username} />
            </div>
          </div>
          <div className="buy-server-row">
            <label htmlFor="pos-server-phone">Cell phone</label>
            <div className="buy-server-control">
              <input
                id="pos-server-phone"
                type="tel"
                aria-invalid={Boolean(fieldErrors.telephone)}
                value={telephone}
                onChange={(e) => {
                  setTelephone(formatPhoneInput(e.target.value));
                  clearField("telephone");
                }}
                placeholder="(555) 123-4567"
                autoComplete="tel"
              />
              <FieldError message={fieldErrors.telephone} />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="buy-server-row">
            <label htmlFor="pos-company">{labeled("Company", true)}</label>
            <div className="buy-server-control">
              <input
                id="pos-company"
                type="text"
                required
                aria-required="true"
                aria-invalid={Boolean(fieldErrors.company_name)}
                value={companyName}
                onChange={(e) => {
                  setCompanyName(e.target.value);
                  clearField("company_name");
                }}
                placeholder="Your company name"
              />
              <FieldError message={fieldErrors.company_name} />
            </div>
          </div>

          <div className="buy-server-row">
            <label htmlFor="pos-first">{labeled("Your name", true)}</label>
            <div className="buy-server-control">
              <div className="buy-pos-name-pair">
                <input
                  id="pos-first"
                  type="text"
                  required
                  aria-required="true"
                  aria-invalid={Boolean(fieldErrors.first_name)}
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    clearField("first_name");
                  }}
                  placeholder="First name"
                  autoComplete="given-name"
                />
                <input
                  id="pos-last"
                  type="text"
                  required
                  aria-required="true"
                  aria-invalid={Boolean(fieldErrors.last_name)}
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    clearField("last_name");
                  }}
                  placeholder="Last name"
                  autoComplete="family-name"
                />
              </div>
              <FieldError message={fieldErrors.first_name || fieldErrors.last_name} />
            </div>
          </div>

          <div className="buy-server-row">
            <label htmlFor="pos-email">{labeled("Email", true)}</label>
            <div className="buy-server-control">
              <input
                id="pos-email"
                type="email"
                required
                aria-required="true"
                aria-invalid={Boolean(fieldErrors.email)}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  clearField("email");
                }}
                placeholder="you@company.com"
                autoComplete="email"
              />
              <FieldError message={fieldErrors.email} />
            </div>
          </div>

          <div className="buy-server-row">
            <label htmlFor="pos-phone">Telephone</label>
            <div className="buy-server-control">
              <input
                id="pos-phone"
                type="tel"
                aria-invalid={Boolean(fieldErrors.telephone)}
                value={telephone}
                onChange={(e) => {
                  setTelephone(formatPhoneInput(e.target.value));
                  clearField("telephone");
                }}
                placeholder="111-222-3333"
                autoComplete="tel"
              />
              <FieldError message={fieldErrors.telephone} />
              <p className="buy-server-product-note">Optional — for important notifications only.</p>
            </div>
          </div>

          <div className="buy-server-row">
            <label htmlFor="pos-code">{labeled("Company code", true)}</label>
            <div className="buy-server-control">
              <input
                id="pos-code"
                type="text"
                required
                aria-required="true"
                aria-invalid={Boolean(fieldErrors.company_code)}
                value={companyCode}
                onChange={(e) => {
                  setCompanyCode(e.target.value.toLowerCase());
                  clearField("company_code");
                }}
                placeholder="acme01"
              />
              <FieldError message={fieldErrors.company_code} />
              <p className="buy-server-product-note">
                4–20 characters, lowercase letters and numbers only.
              </p>
            </div>
          </div>
        </>
      )}

      {error && (
        <p className="model-run-error" role="alert">
          {error}
        </p>
      )}

      <div className="buy-server-actions">
        <button type="submit" className="btn btn-paypal" disabled={loading}>
          {loading
            ? isServer
              ? "Redirecting to PayPal…"
              : "Submitting…"
            : isServer
              ? "Pay with PayPal · $20.00"
              : "Signup for 15 day free trial"}
        </button>
      </div>
    </form>
  );
}
